import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { WizardContainer, WizardNavigation, WizardSummary, WizardSuccess } from '@/components/wizards';
import { Input, Select, Button } from '@/components/common';
import { useWizard } from '@/hooks/useWizard';
import { CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils';
import type { UserSetupWizardData } from '@/types/admin';

const steps = [
  { id: 'basic', title: 'Basic Info' },
  { id: 'role', title: 'Role Assignment' },
  { id: 'permissions', title: 'Permissions' },
  { id: 'review', title: 'Review' },
];

const roles = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full platform access' },
  { value: 'country_admin', label: 'Country Admin', description: 'Manage country operations' },
  { value: 'broker_admin', label: 'Broker Admin', description: 'Manage broker organization' },
  { value: 'broker_employee', label: 'Broker Employee', description: 'Sales and operations staff' },
  { value: 'insurer_admin', label: 'Insurer Admin', description: 'Manage insurer settings' },
  { value: 'insurer_employee', label: 'Insurer Employee', description: 'Underwriting and claims staff' },
  { value: 'posp', label: 'POSP/Agent', description: 'Point of Sales Person' },
];

const tenants = [
  { value: 'broker-1', label: 'ABC Insurance Brokers' },
  { value: 'broker-2', label: 'XYZ Insurance Services' },
  { value: 'insurer-1', label: 'ICICI Lombard' },
  { value: 'insurer-2', label: 'HDFC Ergo' },
];

const permissionGroups = [
  {
    name: 'Users',
    permissions: [
      { id: 'users.view', label: 'View Users' },
      { id: 'users.create', label: 'Create Users' },
      { id: 'users.edit', label: 'Edit Users' },
      { id: 'users.delete', label: 'Delete Users' },
    ],
  },
  {
    name: 'Policies',
    permissions: [
      { id: 'policies.view', label: 'View Policies' },
      { id: 'policies.create', label: 'Create Policies' },
      { id: 'policies.edit', label: 'Edit Policies' },
      { id: 'policies.cancel', label: 'Cancel Policies' },
    ],
  },
  {
    name: 'Claims',
    permissions: [
      { id: 'claims.view', label: 'View Claims' },
      { id: 'claims.create', label: 'Create Claims' },
      { id: 'claims.process', label: 'Process Claims' },
      { id: 'claims.approve', label: 'Approve Claims' },
    ],
  },
  {
    name: 'Reports',
    permissions: [
      { id: 'reports.view', label: 'View Reports' },
      { id: 'reports.export', label: 'Export Reports' },
      { id: 'reports.create', label: 'Create Reports' },
    ],
  },
];

// Default permissions by role
const rolePermissions: Record<string, string[]> = {
  super_admin: permissionGroups.flatMap((g) => g.permissions.map((p) => p.id)),
  country_admin: permissionGroups.flatMap((g) => g.permissions.map((p) => p.id)),
  broker_admin: ['users.view', 'users.create', 'users.edit', 'policies.view', 'policies.create', 'policies.edit', 'claims.view', 'claims.create', 'reports.view', 'reports.export'],
  broker_employee: ['policies.view', 'policies.create', 'claims.view', 'claims.create', 'reports.view'],
  insurer_admin: ['users.view', 'users.create', 'policies.view', 'claims.view', 'claims.process', 'claims.approve', 'reports.view', 'reports.export'],
  insurer_employee: ['policies.view', 'claims.view', 'claims.process', 'reports.view'],
  posp: ['policies.view', 'policies.create', 'claims.view'],
};

function BasicInfoStep({ data, onChange, errors }: {
  data: Partial<UserSetupWizardData>;
  onChange: <K extends keyof UserSetupWizardData>(key: K, value: UserSetupWizardData[K]) => void;
  errors: Record<string, string>;
}) {
  const basicInfo = data.basicInfo || { firstName: '', lastName: '', email: '', phone: '' };

  const updateBasicInfo = (field: string, value: string) => {
    onChange('basicInfo', { ...basicInfo, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Enter the user contact information.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="First Name"
          placeholder="John"
          value={basicInfo.firstName}
          onChange={(e) => updateBasicInfo('firstName', e.target.value)}
          error={errors['basicInfo.firstName']}
          required
        />
        <Input
          label="Last Name"
          placeholder="Doe"
          value={basicInfo.lastName}
          onChange={(e) => updateBasicInfo('lastName', e.target.value)}
          error={errors['basicInfo.lastName']}
          required
        />
        <Input
          label="Email"
          type="email"
          placeholder="john.doe@example.com"
          value={basicInfo.email}
          onChange={(e) => updateBasicInfo('email', e.target.value)}
          error={errors['basicInfo.email']}
          required
        />
        <Input
          label="Phone"
          type="tel"
          placeholder="+91 9876543210"
          value={basicInfo.phone || ''}
          onChange={(e) => updateBasicInfo('phone', e.target.value)}
          error={errors['basicInfo.phone']}
        />
      </div>
    </div>
  );
}

function RoleAssignmentStep({ data, onChange, errors }: {
  data: Partial<UserSetupWizardData>;
  onChange: <K extends keyof UserSetupWizardData>(key: K, value: UserSetupWizardData[K]) => void;
  errors: Record<string, string>;
}) {
  const selectedRole = data.role;
  const needsTenant = selectedRole && !['super_admin', 'country_admin'].includes(selectedRole);

  const handleRoleChange = (roleValue: string) => {
    onChange('role', roleValue);
    // Set default permissions for the role
    onChange('permissions', rolePermissions[roleValue] || []);
    // Clear tenant if not needed
    if (['super_admin', 'country_admin'].includes(roleValue)) {
      onChange('tenantId', undefined);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Role Assignment</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Select the role and organization for this user.
        </p>
      </div>

      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          User Role
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {roles.map((role) => (
            <button
              key={role.value}
              onClick={() => handleRoleChange(role.value)}
              className={cn(
                'p-4 rounded-lg border-2 text-left transition-all relative',
                selectedRole === role.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              )}
            >
              {selectedRole === role.value && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-primary-500 rounded-full flex items-center justify-center">
                  <CheckIcon className="w-3 h-3 text-white" />
                </div>
              )}
              <p className="font-medium text-gray-900 dark:text-white">{role.label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{role.description}</p>
            </button>
          ))}
        </div>
        {errors.role && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
        )}
      </div>

      {/* Tenant Selection (if needed) */}
      {needsTenant && (
        <Select
          label="Organization"
          options={tenants}
          value={data.tenantId || ''}
          onChange={(e) => onChange('tenantId', e.target.value)}
          error={errors.tenantId}
          placeholder="Select organization"
          required
        />
      )}
    </div>
  );
}

function PermissionsStep({ data, onChange }: {
  data: Partial<UserSetupWizardData>;
  onChange: <K extends keyof UserSetupWizardData>(key: K, value: UserSetupWizardData[K]) => void;
}) {
  const selectedPermissions = data.permissions || [];
  const selectedRole = roles.find((r) => r.value === data.role);

  const togglePermission = (permissionId: string) => {
    const newPermissions = selectedPermissions.includes(permissionId)
      ? selectedPermissions.filter((id) => id !== permissionId)
      : [...selectedPermissions, permissionId];
    onChange('permissions', newPermissions);
  };

  const toggleGroup = (group: typeof permissionGroups[0]) => {
    const groupPermissionIds = group.permissions.map((p) => p.id);
    const allSelected = groupPermissionIds.every((id) => selectedPermissions.includes(id));

    if (allSelected) {
      onChange('permissions', selectedPermissions.filter((id) => !groupPermissionIds.includes(id)));
    } else {
      const newPermissions = [...new Set([...selectedPermissions, ...groupPermissionIds])];
      onChange('permissions', newPermissions);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Permissions</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Review and customize permissions for {selectedRole?.label || 'this user'}.
        </p>
      </div>

      <div className="space-y-4">
        {permissionGroups.map((group) => {
          const groupPermissionIds = group.permissions.map((p) => p.id);
          const selectedCount = groupPermissionIds.filter((id) => selectedPermissions.includes(id)).length;
          const allSelected = selectedCount === groupPermissionIds.length;
          const someSelected = selectedCount > 0 && selectedCount < groupPermissionIds.length;

          return (
            <div
              key={group.name}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleGroup(group)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <span className="font-medium text-gray-900 dark:text-white">{group.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedCount}/{groupPermissionIds.length}
                  </span>
                  <div
                    className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                      allSelected
                        ? 'bg-primary-500 border-primary-500'
                        : someSelected
                        ? 'bg-primary-200 border-primary-500'
                        : 'border-gray-300 dark:border-gray-600'
                    )}
                  >
                    {(allSelected || someSelected) && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </button>
              <div className="grid grid-cols-2 gap-2 p-4">
                {group.permissions.map((permission) => {
                  const isSelected = selectedPermissions.includes(permission.id);
                  return (
                    <button
                      key={permission.id}
                      onClick={() => togglePermission(permission.id)}
                      className={cn(
                        'flex items-center gap-2 p-2 rounded-lg text-left transition-colors',
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      )}
                    >
                      <div
                        className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                          isSelected
                            ? 'bg-primary-500 border-primary-500'
                            : 'border-gray-300 dark:border-gray-600'
                        )}
                      >
                        {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {permission.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewStep({ data, onChange }: {
  data: Partial<UserSetupWizardData>;
  onChange: <K extends keyof UserSetupWizardData>(key: K, value: UserSetupWizardData[K]) => void;
}) {
  const selectedRole = roles.find((r) => r.value === data.role);
  const selectedTenant = tenants.find((t) => t.value === data.tenantId);
  const permissionCount = data.permissions?.length || 0;

  const sections = [
    {
      title: 'Basic Information',
      items: [
        { label: 'Full Name', value: `${data.basicInfo?.firstName || ''} ${data.basicInfo?.lastName || ''}`.trim() || '-' },
        { label: 'Email', value: data.basicInfo?.email || '-' },
        { label: 'Phone', value: data.basicInfo?.phone || '-' },
      ],
    },
    {
      title: 'Role & Organization',
      items: [
        { label: 'Role', value: selectedRole?.label || '-' },
        { label: 'Organization', value: selectedTenant?.label || 'N/A' },
        { label: 'Permissions', value: `${permissionCount} permissions granted` },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Review & Create</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Review the user details before creating the account.
        </p>
      </div>

      <WizardSummary sections={sections} />

      {/* Send Invite Option */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <input
          type="checkbox"
          id="sendInvite"
          checked={data.sendInvite !== false}
          onChange={(e) => onChange('sendInvite', e.target.checked)}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label htmlFor="sendInvite" className="text-sm text-gray-700 dark:text-gray-300">
          Send invitation email to the user with login credentials
        </label>
      </div>
    </div>
  );
}

export function UserSetupWizard() {
  const navigate = useNavigate();
  const [isSuccess, setIsSuccess] = useState(false);

  const wizard = useWizard<UserSetupWizardData>({
    steps,
    initialData: { sendInvite: true },
    validateStep: (step, data) => {
      const errors: string[] = [];

      if (step === 0) {
        if (!data.basicInfo?.firstName) errors.push('basicInfo.firstName:First name is required');
        if (!data.basicInfo?.lastName) errors.push('basicInfo.lastName:Last name is required');
        if (!data.basicInfo?.email) errors.push('basicInfo.email:Email is required');
      }

      if (step === 1) {
        if (!data.role) errors.push('role:Please select a role');
        const needsTenant = data.role && !['super_admin', 'country_admin'].includes(data.role);
        if (needsTenant && !data.tenantId) errors.push('tenantId:Please select an organization');
      }

      return errors.length === 0 ? true : errors;
    },
    onComplete: async (data) => {
      // In production, this would call the API to create the user
      void data; // Use the data variable to satisfy linter
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success('User created successfully!');
      setIsSuccess(true);
    },
  });

  if (isSuccess) {
    return (
      <div className="max-w-4xl mx-auto">
        <WizardSuccess
          title="User Created!"
          message={`An invitation email has been sent to ${wizard.data.basicInfo?.email || 'the user'}.`}
          actions={
            <>
              <Button variant="outline" onClick={() => navigate('/admin/users')}>
                View Users
              </Button>
              <Button variant="primary" onClick={() => { wizard.reset(); setIsSuccess(false); }}>
                Create Another
              </Button>
            </>
          }
        />
      </div>
    );
  }

  const renderStepContent = () => {
    switch (wizard.currentStep) {
      case 0:
        return <BasicInfoStep data={wizard.data} onChange={wizard.setStepData} errors={wizard.errors} />;
      case 1:
        return <RoleAssignmentStep data={wizard.data} onChange={wizard.setStepData} errors={wizard.errors} />;
      case 2:
        return <PermissionsStep data={wizard.data} onChange={wizard.setStepData} />;
      case 3:
        return <ReviewStep data={wizard.data} onChange={wizard.setStepData} />;
      default:
        return null;
    }
  };

  return (
    <WizardContainer
      title="User Setup Wizard"
      description="Create a new user account with role and permissions."
      steps={steps}
      currentStep={wizard.currentStep}
      onStepClick={wizard.goToStep}
    >
      {renderStepContent()}

      <WizardNavigation
        isFirst={wizard.isFirst}
        isLast={wizard.isLast}
        isSubmitting={wizard.isSubmitting}
        onPrevious={wizard.goPrevious}
        onNext={wizard.goNext}
        onComplete={wizard.complete}
        completeLabel="Create User"
      />
    </WizardContainer>
  );
}

export default UserSetupWizard;
